import { useState, useEffect, useCallback, useRef } from 'react';
import { Howl } from 'howler';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate a simple beep using base64 encoded WAV
const createToneDataUrl = (frequency, duration, volume = 0.3) => {
  const sampleRate = 22050;
  const numSamples = Math.floor(sampleRate * duration);
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);
  
  // WAV header
  const writeString = (offset, str) => { for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, numSamples * 2, true);
  
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const fadeIn = Math.min(1, t * 20);
    const fadeOut = Math.min(1, (duration - t) * 20);
    const envelope = fadeIn * fadeOut * volume;
    const sample = Math.sin(2 * Math.PI * frequency * t) * envelope * 32767;
    view.setInt16(44 + i * 2, Math.max(-32768, Math.min(32767, sample)), true);
  }
  
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return 'data:audio/wav;base64,' + btoa(binary);
};

const PHASE_TONE_CONFIG = {
  inhale: { freq: 528, dur: 0.4 },
  exhale: { freq: 396, dur: 0.4 },
  hold:   { freq: 440, dur: 0.25 },
  pause:  { freq: 330, dur: 0.25 },
  start:  { freq: 639, dur: 0.5 },
  end:    { freq: 285, dur: 0.6 }
};

const COACH_PHRASES = [
  { id: 'inhale', text: 'Inspirez profondément' },
  { id: 'exhale', text: 'Expirez doucement' },
  { id: 'hold', text: 'Retenez' },
  { id: 'pause', text: 'Relâchez' }
];

export function useBreathingAudio() {
  const [breathingMusic, setBreathingMusic] = useState([]);
  const [ambientSounds, setAmbientSounds] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [musicVolume, setMusicVolume] = useState(0.4);
  const [activeAmbients, setActiveAmbients] = useState({});
  const [ambientVolumes, setAmbientVolumes] = useState({});
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [phaseSoundsEnabled, setPhaseSoundsEnabled] = useState(true);
  const [phaseSoundVolume, setPhaseSoundVolume] = useState(0.5);
  const [voiceGuidanceEnabled, setVoiceGuidanceEnabled] = useState(false);
  const [customVoices, setCustomVoices] = useState({});
  const [maleVoices, setMaleVoices] = useState({});
  const [femaleCoachVoices, setFemaleCoachVoices] = useState({});
  const [femaleTahitianVoices, setFemaleTahitianVoices] = useState({});
  const [selectedVoiceType, setSelectedVoiceType] = useState('male');
  const [lockedVoices, setLockedVoices] = useState(() => {
    try { return JSON.parse(localStorage.getItem('locked_breathing_voices') || '{}'); } catch { return {}; }
  });
  const [voiceVolume, setVoiceVolume] = useState(0.8);
  const [uploadingVoice, setUploadingVoice] = useState(null);
  const [coachGender, setCoachGender] = useState(() => localStorage.getItem('breathing_coach_gender') || 'female');
  const edgeCoachVoicesRef = useRef({});

  const musicRef = useRef(null);
  const ambientRefs = useRef({});
  const phaseSoundRefs = useRef({});
  const voiceRefs = useRef({});
  
  // Refs to avoid stale closures in animation loop
  const voiceGuidanceEnabledRef = useRef(voiceGuidanceEnabled);
  const selectedVoiceTypeRef = useRef(selectedVoiceType);
  const maleVoicesRef = useRef(maleVoices);
  const customVoicesRef = useRef(customVoices);
  const voiceVolumeRef = useRef(voiceVolume);
  const phaseSoundsEnabledRef = useRef(phaseSoundsEnabled);
  const phaseSoundVolumeRef = useRef(phaseSoundVolume);

  const femaleCoachVoicesRef = useRef(femaleCoachVoices);
  const femaleTahitianVoicesRef = useRef(femaleTahitianVoices);

  useEffect(() => { voiceGuidanceEnabledRef.current = voiceGuidanceEnabled; }, [voiceGuidanceEnabled]);
  useEffect(() => { selectedVoiceTypeRef.current = selectedVoiceType; }, [selectedVoiceType]);
  useEffect(() => { maleVoicesRef.current = maleVoices; }, [maleVoices]);
  useEffect(() => { customVoicesRef.current = customVoices; }, [customVoices]);
  useEffect(() => { femaleCoachVoicesRef.current = femaleCoachVoices; }, [femaleCoachVoices]);
  useEffect(() => { femaleTahitianVoicesRef.current = femaleTahitianVoices; }, [femaleTahitianVoices]);
  useEffect(() => { voiceVolumeRef.current = voiceVolume; }, [voiceVolume]);
  useEffect(() => { phaseSoundsEnabledRef.current = phaseSoundsEnabled; }, [phaseSoundsEnabled]);
  useEffect(() => { phaseSoundVolumeRef.current = phaseSoundVolume; }, [phaseSoundVolume]);

  // Preload phase sounds (generated tones)
  useEffect(() => {
    Object.entries(PHASE_TONE_CONFIG).forEach(([key, cfg]) => {
      const url = createToneDataUrl(cfg.freq, cfg.dur);
      phaseSoundRefs.current[key] = new Howl({
        src: [url], volume: phaseSoundVolume, preload: true
      });
    });
    return () => {
      Object.values(phaseSoundRefs.current).forEach(s => { if (s) s.unload(); });
    };
  }, []);

  // Update phase sound volume
  useEffect(() => {
    Object.values(phaseSoundRefs.current).forEach(s => { if (s) s.volume(phaseSoundVolume); });
  }, [phaseSoundVolume]);

  const playPhaseSound = useCallback((action) => {
    if (!phaseSoundsEnabledRef.current) return;
    const sound = phaseSoundRefs.current[action];
    if (sound) { sound.stop(); sound.volume(phaseSoundVolumeRef.current); sound.play(); }
  }, []);

  const changeCoachGender = useCallback((g) => {
    setCoachGender(g);
    localStorage.setItem('breathing_coach_gender', g);
  }, []);

  // Edge TTS natural voices for the Coach
  useEffect(() => {
    axios.post(`${API}/api/edge-tts/generate`, { items: COACH_PHRASES, voice: coachGender })
      .then(res => { edgeCoachVoicesRef.current = res.data.voices || {}; })
      .catch(err => console.error('Edge TTS coach error:', err));
  }, [coachGender]);

  const playVoiceGuidance = useCallback((action) => {
    if (!voiceGuidanceEnabledRef.current) return;
    const vtype = selectedVoiceTypeRef.current;

    // Coach = Edge TTS natural voice
    if (vtype === 'female_coach' && edgeCoachVoicesRef.current[action]) {
      if (voiceRefs.current[action]) voiceRefs.current[action].stop();
      voiceRefs.current[action] = new Howl({
        src: [`${API}${edgeCoachVoicesRef.current[action]}`],
        html5: true, volume: voiceVolumeRef.current
      });
      voiceRefs.current[action].play();
      return;
    }

    let voices;
    if (vtype === 'male') voices = maleVoicesRef.current;
    else if (vtype === 'female_coach') voices = femaleCoachVoicesRef.current;
    else if (vtype === 'female_tahitian') voices = femaleTahitianVoicesRef.current;
    else voices = customVoicesRef.current;
    const voiceUrl = voices[action];
    if (voiceUrl) {
      if (voiceRefs.current[action]) voiceRefs.current[action].stop();
      let fullUrl;
      if (voiceUrl.startsWith('http')) fullUrl = voiceUrl;
      else if (voiceUrl.startsWith('/api/')) fullUrl = `${API}${voiceUrl}`;
      else fullUrl = voiceUrl; // /voices/... served by frontend
      voiceRefs.current[action] = new Howl({
        src: [fullUrl],
        html5: true, volume: voiceVolumeRef.current
      });
      voiceRefs.current[action].play();
    }
  }, []);

  // Fetch audio data
  const fetchAudioData = useCallback(async () => {
    try {
      const [musicRes, ambientRes, voicesRes] = await Promise.all([
        axios.get(`${API}/api/breathing-music`),
        axios.get(`${API}/api/ambient-sounds`),
        axios.get(`${API}/api/breathing-voices`)
      ]);
      setBreathingMusic(musicRes.data);
      setAmbientSounds(ambientRes.data);

      const maleVoicesMap = {};
      const femaleCoachVoicesMap = {};
      const femaleTahitianVoicesMap = {};
      const customVoicesMap = {};
      voicesRes.data.forEach(v => {
        if (v.voice_type === 'male') maleVoicesMap[v.phase_id] = v.audio_url;
        else if (v.voice_type === 'female_coach') femaleCoachVoicesMap[v.phase_id] = v.audio_url;
        else if (v.voice_type === 'female_tahitian') femaleTahitianVoicesMap[v.phase_id] = v.audio_url;
        else customVoicesMap[v.phase_id] = v.audio_url;
      });
      setMaleVoices(maleVoicesMap);
      setFemaleCoachVoices(femaleCoachVoicesMap);
      setFemaleTahitianVoices(femaleTahitianVoicesMap);
      setCustomVoices(customVoicesMap);
      if (Object.keys(maleVoicesMap).length > 0) {
        setVoiceGuidanceEnabled(true);
        setSelectedVoiceType('male');
      }
    } catch (error) {
      console.error('Error fetching audio:', error);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (musicRef.current) musicRef.current.unload();
      Object.values(ambientRefs.current).forEach(s => { if (s) s.unload(); });
      Object.values(voiceRefs.current).forEach(s => { if (s) s.unload(); });
    };
  }, []);

  // Update music volume
  useEffect(() => {
    if (musicRef.current) musicRef.current.volume(musicVolume);
  }, [musicVolume]);

  const playMusic = useCallback((music) => {
    if (musicRef.current) { musicRef.current.stop(); musicRef.current.unload(); }
    if (!music) { setSelectedMusic(null); return; }
    const url = music.file_url
      ? (music.file_url.startsWith('http') ? music.file_url : `${API}${music.file_url}`)
      : music.url;
    musicRef.current = new Howl({ src: [url], html5: true, loop: true, volume: musicVolume });
    musicRef.current.play();
    setSelectedMusic(music);
  }, [musicVolume]);

  const toggleAmbient = useCallback((sound) => {
    const isActive = activeAmbients[sound.id];
    if (isActive) {
      if (ambientRefs.current[sound.id]) {
        ambientRefs.current[sound.id].stop();
        ambientRefs.current[sound.id].unload();
        delete ambientRefs.current[sound.id];
      }
      setActiveAmbients(prev => { const n = { ...prev }; delete n[sound.id]; return n; });
    } else {
      const url = sound.audio_url.startsWith('http') ? sound.audio_url : `${API}${sound.audio_url}`;
      const volume = ambientVolumes[sound.id] || 0.3;
      ambientRefs.current[sound.id] = new Howl({ src: [url], html5: true, loop: true, volume });
      ambientRefs.current[sound.id].play();
      setActiveAmbients(prev => ({ ...prev, [sound.id]: sound }));
      if (!ambientVolumes[sound.id]) setAmbientVolumes(prev => ({ ...prev, [sound.id]: 0.3 }));
    }
  }, [activeAmbients, ambientVolumes]);

  const updateAmbientVolume = useCallback((soundId, volume) => {
    setAmbientVolumes(prev => ({ ...prev, [soundId]: volume }));
    if (ambientRefs.current[soundId]) ambientRefs.current[soundId].volume(volume);
  }, []);

  const handleMusicUpload = async (file) => {
    if (!file) return;
    setUploadingMusic(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
    try {
      const response = await axios.post(`${API}/api/breathing-music/upload`, formData);
      setBreathingMusic(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Error uploading music:', error);
      alert('Erreur lors de l\'upload de la musique');
    } finally { setUploadingMusic(false); }
  };

  const handleDeleteMusic = async (musicId) => {
    try {
      await axios.delete(`${API}/api/breathing-music/${musicId}`);
      setBreathingMusic(prev => prev.filter(m => m.id !== musicId));
      if (selectedMusic?.id === musicId) {
        if (musicRef.current) { musicRef.current.stop(); musicRef.current.unload(); }
        setSelectedMusic(null);
      }
    } catch (error) { console.error('Error deleting music:', error); }
  };

  const handleVoiceUpload = async (phaseId, file, voiceType) => {
    if (!file) return;
    const vt = voiceType || selectedVoiceType;
    setUploadingVoice(phaseId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('voice_type', vt);
    try {
      const response = await axios.post(`${API}/api/breathing-voice/${phaseId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = response.data.audio_url;
      if (vt === 'male') {
        setMaleVoices(prev => ({ ...prev, [phaseId]: url }));
      } else if (vt === 'female_tahitian') {
        setFemaleTahitianVoices(prev => ({ ...prev, [phaseId]: url }));
      } else if (vt === 'female_coach') {
        setFemaleCoachVoices(prev => ({ ...prev, [phaseId]: url }));
      } else {
        setCustomVoices(prev => ({ ...prev, [phaseId]: url }));
      }
      setVoiceGuidanceEnabled(true);
    } catch (error) {
      console.error('Error uploading voice:', error);
      alert('Erreur lors de l\'upload de la voix');
    } finally { setUploadingVoice(null); }
  };

  const handleDeleteVoice = async (phaseId, voiceType) => {
    const vt = voiceType || selectedVoiceType;
    try {
      await axios.delete(`${API}/api/breathing-voice/${phaseId}?voice_type=${vt}`);
      if (vt === 'male') setMaleVoices(prev => { const n = { ...prev }; delete n[phaseId]; return n; });
      else if (vt === 'female_coach') setFemaleCoachVoices(prev => { const n = { ...prev }; delete n[phaseId]; return n; });
      else if (vt === 'female_tahitian') setFemaleTahitianVoices(prev => { const n = { ...prev }; delete n[phaseId]; return n; });
      else setCustomVoices(prev => { const n = { ...prev }; delete n[phaseId]; return n; });
    } catch (error) { console.error('Error deleting voice:', error); }
  };

  const testVoice = (phaseId) => {
    const voiceUrl = customVoices[phaseId];
    if (voiceUrl) {
      new Howl({
        src: [voiceUrl.startsWith('http') ? voiceUrl : `${API}${voiceUrl}`],
        html5: true, volume: voiceVolume
      }).play();
    }
  };

  const stopAllAudio = useCallback(() => {
    if (musicRef.current) { musicRef.current.stop(); musicRef.current.unload(); }
    Object.values(ambientRefs.current).forEach(s => { if (s) { s.stop(); s.unload(); } });
    ambientRefs.current = {};
  }, []);

  const toggleLockVoice = useCallback((key) => {
    setLockedVoices(prev => {
      const upd = { ...prev, [key]: !prev[key] };
      localStorage.setItem('locked_breathing_voices', JSON.stringify(upd));
      return upd;
    });
  }, []);

  return {
    breathingMusic, ambientSounds, selectedMusic, musicVolume, setMusicVolume,
    activeAmbients, ambientVolumes, uploadingMusic,
    phaseSoundsEnabled, setPhaseSoundsEnabled, phaseSoundVolume, setPhaseSoundVolume,
    voiceGuidanceEnabled, setVoiceGuidanceEnabled, customVoices, maleVoices, femaleCoachVoices, femaleTahitianVoices,
    selectedVoiceType, setSelectedVoiceType, voiceVolume, setVoiceVolume, uploadingVoice,
    coachGender, changeCoachGender,
    lockedVoices, toggleLockVoice,
    playPhaseSound, playVoiceGuidance, playMusic, toggleAmbient, updateAmbientVolume,
    handleMusicUpload, handleDeleteMusic, handleVoiceUpload, handleDeleteVoice, testVoice,
    stopAllAudio, fetchAudioData
  };
}
